(() => {
  if (window.NubsCatalogClient) return;
  const script = document.currentScript || document.querySelector('[data-nubs-catalog-client]');
  const bridge = String(script?.dataset.bridgeUrl || '').replace(/\/+$/, '');
  const workerAsset = String(script?.dataset.workerUrl || '');
  if (!bridge) return;
  // Per-tab discovery sequence: a real browser reload gets a new cryptographic
  // seed, but pagination, soft navigation and Back/Forward reuse the same one.
  // Prewarm is async and may run first, so both scripts share this page global.
  function catalogDiscoverySeed() {
    if (/^[0-9a-f]{24}$/.test(String(window.NubsCatalogDiscoverySeed || ''))) return window.NubsCatalogDiscoverySeed;
    const storageKey = 'nubs:catalog-bestmatch-seed:v1';
    let seed = '';
    try {
      const navigation = performance.getEntriesByType('navigation')[0];
      if (navigation?.type !== 'reload') seed = sessionStorage.getItem(storageKey) || '';
      if (!/^[0-9a-f]{24}$/.test(seed)) {
        const bytes = new Uint8Array(12);
        crypto.getRandomValues(bytes);
        seed = [...bytes].map(value => value.toString(16).padStart(2, '0')).join('');
      }
      sessionStorage.setItem(storageKey, seed);
    } catch (_) {
      // The seed is not a credential. Keep discovery usable if storage/crypto
      // is unavailable in a locked-down browser.
      seed = (Math.random().toString(16).slice(2).padEnd(12, '0') + Date.now().toString(16).padStart(12, '0')).slice(0, 24);
    }
    window.NubsCatalogDiscoverySeed = seed;
    return seed;
  }
  const discoveryFilterKeys = ['format','category','releaseType','edition','universe','genres','studios','publisher','audioLanguages','subtitleLanguages','region','rating','year'];
  function isLandingDiscoveryUrl(url) {
    return !url.searchParams.has('mode')
      && (url.searchParams.get('sort') || 'best-match') === 'best-match'
      && !String(url.searchParams.get('q') || '').trim()
      && !discoveryFilterKeys.some(key => url.searchParams.getAll(key).some(value => value.trim()));
  }
  function addDiscoverySeed(url) {
    if (isLandingDiscoveryUrl(url)) {
      url.searchParams.set('discoverySeed', catalogDiscoverySeed());
      url.searchParams.sort(); // Canonical cache key regardless of prewarm/client execution order.
    }
    return url;
  }

  const CACHE_MS = 120000;
  const INDEX_MS = 5 * 60000;
  const MAX_STALE_MS = 60 * 60000;
  const cache = new Map();
  const pending = new Map();
  const workerJobs = new Map();
  let worker = null;
  let workerFailed = false;
  let workerSerial = 0;
  let generation = 0;
  let activeIndex = null;
  let warming = null;
  let warmController = null;
  let lastDiskWrite = 0;
  let candidateController = null;
  let warmTimer = null;
  let dbPromise = null;

  // Search-cover pipeline. Browser/CDN caching remains authoritative; this only
  // remembers work inside the current page session so speculative warming never
  // knowingly repeats the same cover.
  const coverLoaded = new Set();
  const coverInflight = new Map();
  const coverFailures = new Map();
  let speculativeQueue = [];
  let speculativeEpoch = 0;
  let speculativeActive = 0;
  let speculativeIdle = null;
  let speculativeTimer = null;
  const COVER_WIDTHS = [480,640,720,960,1200,1600,2000];
  const COVER_SIZES = '(max-width: 760px) 45vw, (max-width: 1100px) 29vw, 260px';

  function token() {
    try {
      for (const key of ['nubs:catalog-hidden-release-ids:v1','nubs:catalog-visibility-token:v1']) {
        const value = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (value && Number(value.expiresAt) > Date.now()) return String(value.visibilityToken || '');
      }
    } catch (_) {}
    return '';
  }
  function urlFor(input) {
    const url = new URL(String(input), bridge);
    if (url.origin !== new URL(bridge).origin || url.pathname !== '/catalog/releases') return null;
    const entries = [...url.searchParams].sort(([a, av], [b, bv]) =>
      a.localeCompare(b) || (discoveryFilterKeys.includes(a) ? av.localeCompare(bv) : 0));
    url.search = '';
    for (const [key, value] of entries) url.searchParams.append(key, value);
    return url;
  }
  function saveResult(key, result) {
    if (!result.ok || !result.payload?.ok) return;
    cache.delete(key); cache.set(key, {time:Date.now(), result});
    while (cache.size > 48) cache.delete(cache.keys().next().value);
  }
  function abortable(promise, signal) {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
    return new Promise((resolve,reject) => {
      const abort = () => reject(new DOMException('Aborted','AbortError'));
      signal.addEventListener('abort',abort,{once:true});
      promise.then(resolve,reject).finally(() => signal.removeEventListener('abort',abort));
    });
  }
  function getWorker() {
    if (worker || workerFailed) return worker;
    try {
      const url = new URL(workerAsset, location.href);
      // Shopify normally serves theme assets through this same-origin path.
      // Never use blob/eval workers or loosen CSP if an older CDN URL is used.
      if (url.origin !== location.origin && url.pathname.startsWith('/cdn/shop/')) url.host = location.host;
      if (url.origin !== location.origin) { workerFailed = true; return null; }
      worker = new Worker(url.href);
      worker.onmessage = ({data}) => {
        const job = workerJobs.get(data.id);
        if (!job) return;
        workerJobs.delete(data.id); clearTimeout(job.timer);
        if (data.error) job.reject(data.cancelled ? new DOMException('Aborted','AbortError') : new Error(data.error)); else job.resolve(data.result);
      };
      worker.onerror = () => {
        workerFailed = true; worker?.terminate(); worker = null; activeIndex = null;
        for (const job of workerJobs.values()) { clearTimeout(job.timer); job.reject(new Error('Local catalog unavailable')); }
        workerJobs.clear();
      };
    } catch (_) { workerFailed = true; worker = null; }
    return worker;
  }
  function askWorker(type, value, signal) {
    const target = getWorker();
    if (!target) return Promise.reject(new Error('Local catalog unavailable'));
    if (signal?.aborted) return Promise.reject(new DOMException('Aborted','AbortError'));
    const id = ++workerSerial;
    return new Promise((resolve,reject) => {
      const cleanup = () => { clearTimeout(timer); signal?.removeEventListener('abort',abort); workerJobs.delete(id); };
      const fail = error => { cleanup(); reject(error); };
      const abort = () => { target.postMessage({type:'cancel',value:id}); fail(new DOMException('Aborted','AbortError')); };
      const timer = setTimeout(() => { target.postMessage({type:'cancel',value:id}); fail(new Error('Local catalog timed out')); }, 8000);
      workerJobs.set(id,{resolve:value => {cleanup();resolve(value);},reject:fail,timer});
      signal?.addEventListener('abort',abort,{once:true});
      target.postMessage({id,type,value});
    });
  }
  function subscribe(entry, signal) {
    if (signal?.aborted) return Promise.reject(new DOMException('Aborted','AbortError'));
    entry.consumers++;
    return new Promise((resolve,reject) => {
      let finished = false;
      const finish = (callback,value) => {
        if (finished) return;
        finished = true; signal?.removeEventListener('abort',abort);
        entry.consumers--;
        if (!entry.consumers && !entry.settled) entry.controller.abort();
        callback(value);
      };
      const abort = () => finish(reject,new DOMException('Aborted','AbortError'));
      signal?.addEventListener('abort',abort,{once:true});
      entry.promise.then(value=>finish(resolve,value),error=>finish(reject,error));
    });
  }
  function database() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise(resolve => {
      try {
        const request = indexedDB.open('nubs-public-catalog-v1',1);
        request.onupgradeneeded = () => request.result.createObjectStore('snapshots');
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
        request.onblocked = () => resolve(null);
      } catch (_) { resolve(null); }
    });
    return dbPromise;
  }
  async function diskRead() {
    const db = await database(); if (!db) return null;
    return new Promise(resolve => {
      try {
        const store = db.transaction('snapshots').objectStore('snapshots');
        const record = store.get(bridge);
        const verification = store.get(bridge + ':verified');
        verification.onsuccess = () => {
          const saved = record.result;
          const stamp = verification.result;
          if (saved && Number.isFinite(stamp?.checkedAt) && stamp.version === saved.snapshot?.catalog?.version) {
            saved.savedAt = Math.max(saved.savedAt, stamp.checkedAt);
          }
          resolve(saved || null);
        };
        record.onerror = verification.onerror = () => resolve(null);
      } catch (_) { resolve(null); }
    });
  }
  async function updateDiskVerification(version, checkedAt) {
    const db = await database();
    if (!db) return;
    try {
      const tx = db.transaction('snapshots', 'readwrite');
      tx.objectStore('snapshots').put({version, checkedAt}, bridge + ':verified');
      tx.onerror = () => {};
    } catch (_) {}
  }
  async function diskWrite(value) {
    const db = await database(); if (!db) return;
    try { const tx = db.transaction('snapshots','readwrite'); tx.objectStore('snapshots').put(value,bridge); tx.onerror = () => {}; } catch (_) {}
  }
  async function install(snapshot, preference, epoch, checkedAt = Date.now()) {
    if (epoch !== generation || preference !== token() || !snapshot?.items || snapshot.schemaVersion !== 1) return false;
    await askWorker(snapshot.delta ? 'patch' : 'init',snapshot);
    if (epoch !== generation || preference !== token()) return false;
    activeIndex = {preference,version:snapshot.catalog.version,time:checkedAt}; cache.clear();
    document.dispatchEvent(new CustomEvent('nubs:catalog-ready',{detail:{version:snapshot.catalog.version}}));
    return true;
  }
  async function warm() {
    if (warming) return warming;
    if (navigator.connection?.saveData || document.visibilityState === 'hidden' || !getWorker()) return;
    if (activeIndex?.preference === token() && Date.now()-activeIndex.time < INDEX_MS) return;
    const preference = token(); const epoch = generation;
    warmController = new AbortController();
    const refreshSignal = warmController.signal;
    const refreshTimer = setTimeout(()=>warmController?.abort(),25000);
    warming = (async () => {
      let saved = null;
      if (!activeIndex && !preference) {
        saved = await diskRead();
        if (saved && Date.now()-saved.savedAt >= 0 && Date.now()-saved.savedAt < MAX_STALE_MS) {
          lastDiskWrite=saved.savedAt; await install(saved.snapshot,preference,epoch,saved.savedAt);
        }
      }
      if (epoch !== generation || preference !== token()) return;
      const url = new URL(`${bridge}/catalog/releases`);
      url.searchParams.set('mode','snapshot');
      if (preference) url.searchParams.set('visibilityToken',preference);
      if (activeIndex?.preference === preference) url.searchParams.set('knownVersion',activeIndex.version);
      else if (saved?.snapshot?.schemaVersion === 1 && Array.isArray(saved.snapshot.items) && saved.snapshot.catalog?.version) {
        // An old base can prove its version without being shown before validation.
        url.searchParams.set('knownVersion',saved.snapshot.catalog.version);
      }
      const response = await fetch(url, {signal:refreshSignal,credentials:'omit',cache:'default',headers:{Accept:'application/json'}});
      const body = response.ok ? await response.json() : null;
      if (epoch !== generation || preference !== token() || !body?.ok) return;
      if (body.unchanged) {
        const checkedAt = Date.now();
        if (activeIndex?.preference === preference && body.catalog.version === activeIndex.version) {
          activeIndex.time = checkedAt;
        } else if (saved?.snapshot?.catalog?.version === body.catalog.version) {
          await install(saved.snapshot, preference, epoch, checkedAt);
        }
        if (!preference && epoch === generation && preference === token() &&
            activeIndex?.version === body.catalog.version) {
          await updateDiskVerification(body.catalog.version, checkedAt);
        }
      } else {
        if (body.delta && !activeIndex && saved?.snapshot?.catalog?.version === body.baseVersion) {
          // Seed the worker privately. Do not expose the expired base to queries.
          await askWorker('init', saved.snapshot, refreshSignal);
        }
        if (!await install(body,preference,epoch)) return;
        // Persist public/default data only. Personalized subsets stay in memory.
        if (!preference && (!body.delta || Date.now()-lastDiskWrite > 30*60000)) {
          const savedSnapshot = body.delta ? await askWorker('snapshot',null,refreshSignal) : body;
          if (epoch === generation && preference === token()) {
            lastDiskWrite=Date.now(); diskWrite({savedAt:lastDiskWrite,snapshot:savedSnapshot});
          }
        }
      }
    })().catch(() => {}).finally(() => { clearTimeout(refreshTimer); warming = null; warmController = null; });
    return warming;
  }
  function scheduleWarm() {
    if (warmTimer || warming) return;
    warmTimer = setTimeout(() => {
      warmTimer = null;
      if ('requestIdleCallback' in window) requestIdleCallback(() => warm(),{timeout:3000});
      else warm();
    }, 250);
  }
  function inputFor(url) {
    const filters = {};
    for (const key of ['format','category','releaseType','edition','universe','genres','studios','publisher','audioLanguages','subtitleLanguages','region','rating','year']) {
      const values = url.searchParams.getAll(key).flatMap(value => value.split('||')).map(value => value.trim()).filter(Boolean).slice(0,50);
      if (values.length) filters[key] = values;
    }
    return {q:url.searchParams.get('q') || '',page:url.searchParams.get('page'),pageSize:url.searchParams.get('pageSize'),sort:url.searchParams.get('sort') || 'best-match',filters,includeFacets:url.searchParams.get('facets') !== '0',pinLandingBestMatch:true,discoverySeed:url.searchParams.get('discoverySeed') || '',featuredCatalogIds:(url.searchParams.get('featured') || '').split(',').filter(Boolean).slice(0,7)};
  }
  function request(input, {signal, force = false} = {}) {
    if (signal?.aborted) return Promise.reject(new DOMException('Aborted','AbortError'));
    const url = urlFor(input);
    if (url) addDiscoverySeed(url);
    if (!url || url.searchParams.get('mode') === 'visibility-list') return Promise.reject(new Error('Expected a public catalog request'));
    const key = url.href; const mode = url.searchParams.get('mode') || '';
    const preference = url.searchParams.get('visibilityToken') || '';
    const serverOnlySort = ['most-collected','most-wanted'].includes(String(url.searchParams.get('sort') || ''));
    const cached = cache.get(key);
    if (!force && cached && Date.now()-cached.time < (serverOnlySort ? 15000 : CACHE_MS)) return abortable(Promise.resolve(cached.result),signal);
    const previous = pending.get(key);
    if (previous && !previous.controller.signal.aborted) return subscribe(previous,signal);
    const epoch = generation;
    const entry = {controller:new AbortController(),consumers:0,settled:false,promise:null};
    const workSignal = entry.controller.signal;
    entry.promise = (async () => {
      if (!force && !mode && !serverOnlySort && activeIndex?.preference === preference && preference === token() && Date.now()-activeIndex.time < MAX_STALE_MS) {
        if (Date.now()-activeIndex.time >= INDEX_MS) scheduleWarm();
        try {
          const payload = await askWorker('query',inputFor(url),workSignal);
          if (epoch === generation && preference === token()) {
            const result = {ok:true,status:200,payload}; saveResult(key,result); return result;
          }
        } catch (error) { if (error.name === 'AbortError') throw error; }
      }
      const response = await fetch(url,{signal:workSignal,credentials:'omit',cache:serverOnlySort?'no-store':force?'reload':'default',headers:{Accept:'application/json'}});
      const result = {ok:response.ok,status:response.status,payload:await response.json().catch(error=>{if(error.name==='AbortError')throw error;return null;})};
      if (epoch === generation) saveResult(key,result);
      if (!mode) scheduleWarm();
      return result;
    })().finally(() => { entry.settled=true; if (pending.get(key) === entry) pending.delete(key); });
    pending.set(key,entry);
    return subscribe(entry,signal);
  }
  function shopifyImageUrl(url, width) {
    if (!url) return '';
    try {
      const parsed = new URL(String(url), location.href);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (_) { return String(url); }
  }
  function coverKey(url) {
    if (!url) return '';
    try {
      const parsed = new URL(String(url), location.href);
      parsed.searchParams.delete('width');
      return parsed.toString();
    } catch (_) { return String(url); }
  }
  function coverProfileKey(url, profile = 'grid') {
    const key = coverKey(url);
    return key ? `${key}|${profile === 'search' ? 'search' : 'grid'}` : '';
  }
  function coverSrcset(url) {
    return COVER_WIDTHS.map(width => `${shopifyImageUrl(url,width)} ${width}w`).join(', ');
  }
  function connectionPolicy() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection?.saveData) return {enabled:false,concurrency:0,max:0};
    const type = String(connection?.effectiveType || '').toLowerCase();
    if (type === 'slow-2g' || type === '2g') return {enabled:false,concurrency:0,max:0};
    if (type === '3g') return {enabled:true,concurrency:1,max:4};
    return {enabled:true,concurrency:2,max:16};
  }
  function markCoverLoaded(url, profile = 'grid') {
    const key = coverProfileKey(url,profile);
    if (key) { coverLoaded.add(key); coverFailures.delete(key); }
  }
  function revealImage(img) {
    if (!(img instanceof HTMLImageElement) || img.dataset.nubsImageObserved === '1') return;
    img.dataset.nubsImageObserved = '1';
    const original = img.dataset.nubsCoverUrl || img.currentSrc || img.src || '';
    const profile = img.dataset.nubsImageProfile === 'search' ? 'search' : 'grid';
    const finish = (loaded = true) => {
      if (img.dataset.nubsImageReady === '1') return;
      img.dataset.nubsImageReady = '1';
      img.classList.add('nubs-image-ready');
      if (loaded) markCoverLoaded(original || img.currentSrc || img.src,profile);
    };
    const decodeAndFinish = () => {
      if (!img.decode) { finish(true); return; }
      img.decode().then(() => finish(true)).catch(() => finish(true));
    };
    if (img.complete) {
      if (img.naturalWidth > 0) decodeAndFinish();
      else finish(false);
      return;
    }
    img.addEventListener('load',decodeAndFinish,{once:true});
    // Genuine failures may show normal alt text rather than leaving a blank box.
    img.addEventListener('error',()=>finish(false),{once:true});
  }
  function observeImages(root = document) {
    if (!root?.querySelectorAll) return;
    if (root instanceof HTMLImageElement && root.matches('[data-nubs-image-reveal]')) revealImage(root);
    root.querySelectorAll('img[data-nubs-image-reveal]').forEach(revealImage);
  }
  function clearSpeculativeSchedule() {
    if (speculativeIdle != null && 'cancelIdleCallback' in window) {
      try { cancelIdleCallback(speculativeIdle); } catch (_) {}
    }
    speculativeIdle = null;
    if (speculativeTimer != null) clearTimeout(speculativeTimer);
    speculativeTimer = null;
  }
  function cancelSpeculation() {
    candidateController?.abort(); candidateController=null;
    speculativeEpoch += 1;
    speculativeQueue = [];
    clearSpeculativeSchedule();
  }
  function speculationBudget(result, policy, query) {
    const matching = Math.max(0, Number(result?.matchingCount) || 0);
    const qLength = compactQueryLength(query);
    let budget;
    if (matching > 100) budget = qLength <= 1 ? 6 : qLength === 2 ? 8 : 10;
    else if (matching <= 12) budget = 12;
    else if (matching <= 30) budget = 14;
    else if (matching <= 60) budget = 12;
    else budget = 10;
    return Math.min(policy.max, budget);
  }
  function compactQueryLength(value) {
    return String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g,'').toLowerCase().replace(/&/g,'and').replace(/[^a-z0-9]+/g,'').length;
  }
  function scheduleSpeculativePump(epoch) {
    if (epoch !== speculativeEpoch || !speculativeQueue.length) return;
    clearSpeculativeSchedule();
    const run = () => { speculativeIdle = null; speculativeTimer = null; pumpSpeculation(epoch); };
    if ('requestIdleCallback' in window) speculativeIdle = requestIdleCallback(run,{timeout:900});
    else speculativeTimer = setTimeout(run,90);
  }
  function finishSpeculative(key, success, epoch) {
    coverInflight.delete(key);
    speculativeActive = Math.max(0,speculativeActive-1);
    if (success) { coverLoaded.add(key); coverFailures.delete(key); }
    else coverFailures.set(key,Date.now()+60000);
    if (speculativeQueue.length) scheduleSpeculativePump(speculativeEpoch);
  }
  function startSpeculative(candidate, epoch) {
    const url = String(candidate?.cover?.url || '');
    const profile = candidate?._nubsProfile === 'search' ? 'search' : 'grid';
    const key = coverProfileKey(url,profile);
    if (!url || !key || coverLoaded.has(key) || coverInflight.has(key)) return;
    const failedUntil = Number(coverFailures.get(key) || 0);
    if (failedUntil > Date.now()) return;
    if (failedUntil) coverFailures.delete(key);

    const image = new Image();
    speculativeActive += 1;
    coverInflight.set(key,image);
    try { image.fetchPriority = 'low'; } catch (_) {}
    image.decoding = 'async';
    image.onload = () => {
      if (!image.decode) { finishSpeculative(key,true,epoch); return; }
      image.decode().then(()=>finishSpeculative(key,true,epoch)).catch(()=>finishSpeculative(key,true,epoch));
    };
    image.onerror = () => finishSpeculative(key,false,epoch);
    if (profile === 'search') {
      image.src = shopifyImageUrl(url,320);
    } else {
      image.sizes = COVER_SIZES;
      image.srcset = coverSrcset(url);
      // Set src last so srcset/sizes are already available when selection starts.
      image.src = shopifyImageUrl(url,1200);
    }
  }
  function pumpSpeculation(epoch) {
    if (epoch !== speculativeEpoch || document.visibilityState === 'hidden') return;
    const policy = connectionPolicy();
    if (!policy.enabled) { speculativeQueue = []; return; }
    while (epoch === speculativeEpoch && speculativeActive < policy.concurrency && speculativeQueue.length) {
      const candidate = speculativeQueue.shift();
      startSpeculative(candidate,epoch);
    }
    if (speculativeQueue.length && speculativeActive < policy.concurrency) scheduleSpeculativePump(epoch);
  }
  async function speculateCatalog(query, {visibleItems = [], filters = {}, profile = 'grid'} = {}) {
    cancelSpeculation();
    const q = String(query || '').trim();
    const policy = connectionPolicy();
    if (!q || !policy.enabled || document.visibilityState === 'hidden') return;
    if (!activeIndex || activeIndex.preference !== token()) return;
    if (Date.now()-activeIndex.time >= INDEX_MS) scheduleWarm();
    const epoch = speculativeEpoch;
    const excludeIds = (visibleItems || []).map(item=>String(item?.id || item?.catalogId || '')).filter(Boolean);
    let result;
    candidateController=new AbortController();
    try { result = await askWorker('image-candidates',{q,filters,limit:32,excludeIds},candidateController.signal); } catch (_) { return; }
    if (epoch !== speculativeEpoch || document.visibilityState === 'hidden') return;

    const normalizedProfile = profile === 'search' ? 'search' : 'grid';
    const visible = new Set((visibleItems || []).map(item=>coverProfileKey(item?.cover?.url || '',normalizedProfile)).filter(Boolean));
    const budget = speculationBudget(result,policy,q);
    const queued = new Set();
    speculativeQueue = (Array.isArray(result?.candidates) ? result.candidates : []).filter(candidate => {
      const key = coverProfileKey(candidate?.cover?.url || '',normalizedProfile);
      if (!key || visible.has(key) || coverLoaded.has(key) || coverInflight.has(key) || queued.has(key)) return false;
      const failedUntil = Number(coverFailures.get(key) || 0);
      if (failedUntil > Date.now()) return false;
      queued.add(key);
      candidate._nubsProfile = normalizedProfile;
      return true;
    }).slice(0,budget);
    scheduleSpeculativePump(epoch);
  }

  window.NubsCatalogImages = {
    observe: observeImages,
    speculate: speculateCatalog,
    cancelSpeculation,
    markLoaded: markCoverLoaded,
    get status(){ return {loaded:coverLoaded.size,inflight:coverInflight.size,queued:speculativeQueue.length}; }
  };

  function clear() {
    cancelSpeculation();
    generation++; activeIndex = null; cache.clear();
    warmController?.abort();
    for(const entry of pending.values())entry.controller.abort();
    pending.clear();
    if (worker) { worker.terminate(); worker = null; }
    for (const job of workerJobs.values()) {clearTimeout(job.timer);job.reject(new Error('Catalog preferences changed'));}
    workerJobs.clear();
  }
  function prefetchPage(input) {
    const page=new URL(input,location.href);
    if(page.pathname.replace(/^\/(fr|es)(?=\/)/,'')!=='/pages/my-collection' || ['owned','wanted','uncovered'].includes(page.searchParams.get('library')) || page.searchParams.get('username'))return;
    const url=new URL(`${bridge}/catalog/releases`);
    for(const [key,value] of page.searchParams){
      if(['q','page','sort'].includes(key))url.searchParams.set(key,value);
      if(key.startsWith('filter_'))url.searchParams.append(key.slice(7),value);
    }
    for(const [from,to] of [['category','category'],['universe','universe'],['genre','genres'],['studio','studios'],['publisher','publisher'],['format','format']]){
      const value=page.searchParams.get(from);if(value)url.searchParams.append(to,value);
    }
    if(!url.searchParams.has('page'))url.searchParams.set('page','1');
    if(!url.searchParams.has('sort'))url.searchParams.set('sort','best-match');
    url.searchParams.set('pageSize','48');
    if(token())url.searchParams.set('visibilityToken',token());
    // The real landing request includes the destination's featured IDs.
    if (!isLandingDiscoveryUrl(url)) request(url).catch(()=>{});
  }
  window.NubsCatalogClient = {
    request,warm,scheduleWarm,token,clear,prefetchPage,
    get ready(){return Boolean(activeIndex && activeIndex.preference===token());},
    get fresh(){return Boolean(activeIndex && activeIndex.preference===token() && Date.now()-activeIndex.time<INDEX_MS);}
  };
  window.addEventListener('nubs:catalog-visibility-changed',clear);
  document.addEventListener('nubs:session-cleared',clear);
  document.addEventListener('nubs:beforepagechange',cancelSpeculation);
  window.addEventListener('pagehide',cancelSpeculation);
  document.addEventListener('focusin',event => {if(event.target.matches?.('[data-site-search-input], [data-collector-search]'))scheduleWarm();});
  document.addEventListener('visibilitychange',() => {
    if(document.visibilityState==='hidden') cancelSpeculation();
    else if(activeIndex) scheduleWarm();
  });
})();
